"use client";

import { useState, type FC, type Ref } from "react";
import { useTranslation } from "react-i18next";

import { Image } from "../Image";
import { Modal } from "../Modal";
import { GalleryCarousel } from "../GalleryCarousel";

import { galleryStyle, itemStyle, featuredItemStyle } from "./Gallery.css";
import { Button } from "../Button";
import { Flex } from "../Flex";

export type GalleryImage =
  | string
  | {
      url: string;
      /**
       * Low-resolution URL for thumbnail strips (e.g. 180px wide)
       */
      thumbnail?: string;
      title?: string;
      hotspots?: {
        left: number;
        top: number;
        title?: string;
        subtitle?: string;
      }[];
    };

export type GalleryProps = {
  images: GalleryImage[];
  title?: string;
  showAll?: boolean;
  variant?: "default";
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * Rename to image or gallery pentaptych
 */
export const Gallery: FC<GalleryProps> = ({
  images,
  title,
  showAll = true,
  variant = "default",
  ref,
}) => {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t("gallery.defaultTitle");
  const imageSet = images.slice(0, 5);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [defaultImage, setDefaultImage] = useState(0);

  return (
    <>
      {variant === "default" && (
        <Flex direction="column" gap="l">
          <div ref={ref} className={galleryStyle()}>
            {imageSet.map((image, i) => {
              const src = typeof image === "string" ? image : image.url;

              return (
                <Image
                  src={src}
                  key={i}
                  className={i === 0 ? featuredItemStyle : itemStyle}
                  borderRadius
                  lazy
                  alt={resolvedTitle}
                  onClick={() => {
                    setDefaultImage(i);
                    setIsModalOpen(true);
                  }}
                />
              );
            })}
          </div>
          {showAll && (
            <Button
              onClick={() => setIsModalOpen(true)}
              alignSelf="center"
              variant="opaque"
              border="default"
            >
              {t("gallery.viewAllPhotos", { count: images.length })}
            </Button>
          )}
        </Flex>
      )}

      {isModalOpen && (
        <Modal
          escape
          closeable
          size="large"
          title={resolvedTitle}
          onClose={() => setIsModalOpen(false)}
        >
          {images && images.length > 0 && (
            <GalleryCarousel
              items={imageSet.map((image, i) => ({
                url: typeof image === "string" ? image : image.url,
                reference:
                  typeof image === "string"
                    ? t("gallery.image", { index: i + 1 })
                    : (image.title ?? t("gallery.image", { index: i + 1 })),
              }))}
              default={defaultImage}
            />
          )}
        </Modal>
      )}
    </>
  );
};
